import { useAppContext } from "AppContext";
import { useUserUpdateMutation } from "gql/generated";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getNameFromUser } from "shared/utils";

type IWelcomeMessage = {
  emoji: string;
  headingText: string;
  message: string;
  buttonText: string;
};

export const usePostOnboarding = () => {
  const { t } = useTranslation();
  const { dbUser, refetchDbUser } = useAppContext();
  const navigate = useNavigate();

  const [welcomeMessage, setWelcomeMessage] = useState<IWelcomeMessage>();
  const [screenCounter, setScreenCounter] = useState(0);

  const welcomeMessages: IWelcomeMessage[] = useMemo(() => {
    const welcomeMessages = [
      {
        emoji: "👋",
        headingText: `${t("core:DOORSTOP.HI")}, ${getNameFromUser(dbUser!, true)}!`,
        message: t("postOnboarding:SCREENS.INTRO"),
        buttonText: t("postOnboarding:CTAS.LETS_GO"),
      },
      {
        emoji: "🤝",
        headingText: t("postOnboarding:SCREENS.COLLABORATE.TITLE"),
        message: t("postOnboarding:SCREENS.COLLABORATE.DESCRIPTION"),
        buttonText: t("postOnboarding:CTAS.NEXT"),
      },
      {
        emoji: "🖼️",
        headingText: t("postOnboarding:SCREENS.SHINE.TITLE"),
        message: t("postOnboarding:SCREENS.SHINE.DESCRIPTION"),
        buttonText: t("postOnboarding:CTAS.NEXT"),
      },
      {
        emoji: "✉️",
        headingText: t("postOnboarding:SCREENS.INVITE.TITLE"),
        message: t("postOnboarding:SCREENS.INVITE.DESCRIPTION"),
        buttonText: t("postOnboarding:CTAS.NEXT"),
      },
      {
        emoji: "🏘️",
        headingText: t("postOnboarding:SCREENS.MANAGE.TITLE"),
        message: t("postOnboarding:SCREENS.MANAGE.DESCRIPTION"),
        buttonText: t("postOnboarding:CTAS.LETS_GO"),
      },
    ];

    if (dbUser) {
      setWelcomeMessage(welcomeMessages[0]);
    }
    return welcomeMessages;
  }, [dbUser, t]);

  /**
   * Handle clicking through the welcome screens
   * - When the final "Let's go!" button is clicked,
   *   update the user record so they don't need to go through these screens again
   */
  const [userUpdateMutation] = useUserUpdateMutation();
  const handleNextClick = useCallback(async () => {
    const nextScreen = Math.min(welcomeMessages.length - 1, screenCounter + 1);
    if (nextScreen !== screenCounter + 1) {
      // Update the user's record in the DB
      // And navigate back to the <App /> component
      await userUpdateMutation({
        variables: {
          id: dbUser?._id,
          record: {
            onboardingMetadata: {
              ...dbUser?.onboardingMetadata,
              isPostOnboarding: true,
            },
          },
        },
      });
      await refetchDbUser();
      navigate(`/app?email=${dbUser?.email.toLowerCase()}`, { replace: true });
    } else {
      setScreenCounter(nextScreen);
      setWelcomeMessage(welcomeMessages[nextScreen]);
    }
  }, [
    dbUser,
    screenCounter,
    welcomeMessages,
    navigate,
    refetchDbUser,
    setScreenCounter,
    setWelcomeMessage,
    userUpdateMutation,
  ]);

  /**
   * If dbUser.messages.welcome === true navigate back to <App />
   * - In case someone types /app/welcome into the URL bar
   */
  useEffect(() => {
    if (dbUser && dbUser.onboardingMetadata?.isPostOnboarding) {
      navigate(`/app`, { replace: true });
    }
  }, [dbUser, navigate]);

  return {
    welcomeMessage,
    screenCounter,
    handleNextClick,
  };
};
